import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Avatar, 
  CircularProgress, 
  Grid, 
  Paper, 
  Chip, 
  Stack, 
  Container, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import ProfileService from '../services/ProfileService';
import JobService from '../services/JobService';
import DocumentService from '../services/DocumentService';
import { useAuth } from '../services/AuthService';
import { useTheme } from '@mui/material/styles';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LanguageIcon from '@mui/icons-material/Language';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedIcon from '@mui/icons-material/Verified';
import FlagIcon from '@mui/icons-material/Flag';
import LockIcon from '@mui/icons-material/Lock';
import AdminService from '../services/AdminService';
import FeedbackDialog from '../components/FeedbackDialog';
import FilePreviewDialog, { isPreviewableFile } from '../components/FilePreviewDialog';
import { useUserType } from '../hooks/useUserType';
import {
  skillOptions,
  availabilityOptions,
  salaryOptions,
  visaOptions,
  transportOptions,
  experienceOptions,
  englishLevelOptions,
} from '../constants/dropdownOptions';
import { useNavigate } from 'react-router-dom';

const getLabel = (value, options) => {
  if (!value) return null;
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : value;
};

const DetailRow = ({ label, value, icon, chipColor }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon && React.cloneElement(icon, { fontSize: 'small', color: 'action' })}
      {chipColor ? (
        <Chip 
          label={value} 
          color={chipColor} 
          size="small" 
          variant={chipColor === 'default' ? 'outlined' : 'filled'}
          sx={{ fontWeight: 600, border: '1px solid', borderColor: `${chipColor}.light`, boxShadow: (t) => t.palette.mode === 'dark' ? `0 0 8px ${t.palette[chipColor]?.main || 'transparent'}40` : 'none' }}
        />
      ) : (
        <Typography variant="body1" fontWeight="medium">
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

const JobSeekerPublicPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  const { user, isAuthenticated, loginWithRedirect, signupWithRedirect } = useAuth();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [jobSeekerData, setJobSeekerData] = useState(null);
  const [jobPreview, setJobPreview] = useState(null);
  const [documentsList, setDocumentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [canShortlist, setCanShortlist] = useState(false);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [accessLevel, setAccessLevel] = useState('public'); // 'public', 'company', 'jobseeker', 'admin'
  const [blurredSections, setBlurredSections] = useState({});
  const [loginMessage, setLoginMessage] = useState(null);
  const [avatarBlurred, setAvatarBlurred] = useState(false);
  const theme = useTheme();

  const isAdmin = isAuthenticated && user?.groups?.includes('admin');

  const handleManageJobseeker = async () => {
    try {
      const { url } = await AdminService.getAdminNavigationUrl('manage-jobseeker', { profileId: id });
      navigate(url);
    } catch (err) {
      console.error('Failed to get admin navigation URL:', err);
      setSnackbar({ open: true, message: 'Failed to navigate to admin panel', severity: 'error' });
    }
  };
  const { userTypeData } = useUserType();
  const isCompany = userTypeData?.userType === 'company';
  const isSuspended = jobSeekerData?.accountStatus === 'suspended';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const jobSeekerService = new JobSeekerProfileService(null);
        const jobService = new JobService(null);
        const documentService = new DocumentService(null);

        // Parallel fetch for job seeker data, avatar, and job preview (if jobId exists)
        const promises = [
          jobSeekerService.getRecordById(id),
          documentService.getAvatarByProfileId(id),
        ];

        if (jobId) {
          promises.push(jobService.getJobById(jobId).catch(err => {
            console.error("Error fetching job preview:", err);
            return null;
          }));
        }

        const results = await Promise.all(promises);
        const jobSeeker = results[0];
        const avatarResp = results[1] || { avatarUrl: null };
        const jobData = results.length > 2 ? results[2] : null;

        setAvatarUrl(avatarResp.avatarUrl || null);

        if (jobData) {
          setJobPreview(jobData);
        }

        // Check if current user can shortlist (owns the job)
        if (jobId && isAuthenticated && user) {
          try {
            const jobService = new JobService(user);
            const shortlisted = await jobService.getShortlistedApplicants(jobId);
            setCanShortlist(true);
            setIsShortlisted(shortlisted.includes(id));
          } catch (err) {
            // User doesn't own this job or not authenticated — hide shortlist
            setCanShortlist(false);
          }
        }

        if (jobSeeker) {
          setJobSeekerData(jobSeeker);
          
          // Extract access level and visibility info from response
          setAccessLevel(jobSeeker.accessLevel || 'public');
          setBlurredSections(jobSeeker.blurredSections || {});
          setLoginMessage(jobSeeker.message || null);
          setAvatarBlurred(jobSeeker.avatarBlurred || false);

          // Extract documents from response (now included in API response for authenticated users)
          if (jobSeeker.documents && Array.isArray(jobSeeker.documents)) {
            setDocumentsList(jobSeeker.documents);
          }

          // Use basicProfile enriched by the job seeker API when available
          if (jobSeeker.basicProfile) {
            setProfileData(jobSeeker.basicProfile);
          }
        }

      } catch (error) {
        console.error("Error fetching public profile:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id, jobId, isAuthenticated, user]);

  const handleToggleShortlist = useCallback(async () => {
    if (!jobId || !id || shortlistLoading) return;
    setShortlistLoading(true);
    try {
      const jobService = new JobService(user);
      if (isShortlisted) {
        await jobService.removeFromShortlist(jobId, id);
        setIsShortlisted(false);
        setSnackbar({ open: true, message: 'Removed from shortlist', severity: 'info' });
      } else {
        await jobService.addToShortlist(jobId, id);
        setIsShortlisted(true);
        setSnackbar({ open: true, message: 'Added to shortlist', severity: 'success' });
      }
    } catch (err) {
      console.error('Shortlist toggle failed:', err);
      setSnackbar({ open: true, message: 'Failed to update shortlist', severity: 'error' });
    } finally {
      setShortlistLoading(false);
    }
  }, [jobId, id, isShortlisted, shortlistLoading, user]);

  const handleAdminDownload = async (docId) => {
    setDownloadingDocId(docId);
    // Open a blank window synchronously to preserve the user gesture (prevents popup blocking on mobile)
    const newWindow = window.open('', '_blank');
    try {
      const { downloadUrl } = await AdminService.getDocumentDownloadUrl(docId);
      if (newWindow) {
        newWindow.location = downloadUrl;
      } else {
        // Fallback if window couldn't be opened
        window.location.href = downloadUrl;
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      console.error('Download failed:', err);
      setSnackbar({ open: true, message: 'Failed to download document.', severity: 'error' });
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleAdminPreview = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const { downloadUrl } = await AdminService.getDocumentDownloadUrl(doc.id);
      setPreviewDocument({ ...doc, downloadUrl });
    } catch (err) {
      console.error('Preview failed:', err);
      setSnackbar({ open: true, message: 'Failed to preview document.', severity: 'error' });
    } finally {
      setDownloadingDocId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData && !jobSeekerData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" flexDirection="column">
         <Typography variant="h5" color="text.secondary">Profile unavailable</Typography>
      </Box>
    );
  }

  if (isSuspended) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" flexDirection="column">
        <Typography variant="h5" color="text.secondary">This profile is no longer available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Job Context Banner */}
        {jobPreview && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'primary.main',
              bgcolor: 'rgba(25, 118, 210, 0.04)' // subtle primary tint
            }}
          >
             <Box>
                 <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                   Potential Match For
                 </Typography>
                 <Typography variant="body1" sx={{ 
                   color: 'text.primary',
                   fontWeight: 500,
                   fontSize: '1rem'
                 }}>
                   {jobPreview.description}
                 </Typography>
             </Box>
          </Paper>
        )}

        {/* Header Section */}
        <Paper elevation={0} sx={{ p: 4, mb: 3, borderRadius: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
          {avatarUrl ? (
            <Avatar
              src={avatarUrl}
              sx={{ width: 120, height: 120, fontSize: 48, boxShadow: theme.shadows[3], filter: avatarBlurred ? 'blur(12px)' : 'none' }}
            />
          ) : (
            <Avatar 
              sx={{ 
                width: 120, 
                height: 120, 
                fontSize: 48, 
                bgcolor: theme.palette.primary.main,
                boxShadow: theme.shadows[3],
                filter: avatarBlurred ? 'blur(12px)' : 'none'
              }}
            >
              {profileData?.firstName ? profileData.firstName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          )}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography variant="h3" fontWeight="bold" color="text.primary">
                {profileData?.firstName || 'Candidate'}
              </Typography>
              {jobSeekerData?.skillsVerified && (
                <Tooltip title="Skills Verified by Labourando">
                  <VerifiedIcon sx={{ fontSize: 28, color: 'primary.main', ml: 0.5 }} />
                </Tooltip>
              )}
              {isCompany && jobSeekerData?.noShowReportCount > 0 && (
                <Tooltip title="This profile has been reported for No Shows">
                  <FlagIcon sx={{ fontSize: 28, color: 'error.main', ml: 0.5 }} />
                </Tooltip>
              )}
              {isCompany && !isSuspended && (
                <Tooltip title="Leave feedback for this worker">
                  <Button
                    onClick={() => setFeedbackDialogOpen(true)}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<RateReviewIcon />}
                    sx={{ ml: 1 }}
                  >
                    Leave Feedback
                  </Button>
                </Tooltip>
              )}
              {canShortlist && jobId && !isSuspended && (
                <Tooltip title={isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'}>
                  <IconButton
                    onClick={handleToggleShortlist}
                    disabled={shortlistLoading}
                    sx={{
                      color: isShortlisted ? 'warning.main' : 'action.disabled',
                      '&:hover': { color: 'warning.main' },
                    }}
                  >
                    {shortlistLoading ? (
                      <CircularProgress size={24} />
                    ) : isShortlisted ? (
                      <StarIcon fontSize="large" />
                    ) : (
                      <StarBorderIcon fontSize="large" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ color: 'text.secondary' }}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <LocationOnIcon fontSize="small" />
                <Typography variant="body1">
                  {jobSeekerData?.location || jobSeekerData?.suburb || 'Location N/A'}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>


        <Grid container spacing={3}>
          {/* Main Content Column */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={3}>
                {/* Bio Section */}
                {jobSeekerData?.bioDescription && !blurredSections?.bio && (
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="action" /> Bio
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
                        {jobSeekerData.bioDescription}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              
              {/* Skills Section - Always visible */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <VerifiedUserIcon color="action" /> Skills
                </Typography>
                <Divider sx={{ mb: 2 }} />
                  
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                   {jobSeekerData?.skills?.length > 0 ? (
                      jobSeekerData.skills.map((skill, index) => (
                        <Chip key={index} label={getLabel(skill, skillOptions)} color="primary" variant="filled" sx={{ borderRadius: 1, fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText', border: '1px solid', borderColor: 'primary.light', boxShadow: (t) => t.palette.mode === 'dark' ? '0 0 8px hsla(145,72%,40%,0.35)' : 'none' }} />
                      ))
                   ) : (
                     <Typography color="text.secondary">No skills listed.</Typography>
                   )}
                </Stack>
              </Paper>
               
              {/* Attributes Section - Always visible */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <CheckCircleOutlineIcon color="action" /> Credentials & Attributes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" flexWrap="wrap" gap={1}>
                   {jobSeekerData?.labourRequirements?.length > 0 ? (
                      jobSeekerData.labourRequirements.map((attr, index) => (
                        <Chip key={index} label={attr} variant="filled" color="success" size="small" sx={{ borderRadius: 1, fontWeight: 600, border: '1px solid', borderColor: 'success.light', boxShadow: (t) => t.palette.mode === 'dark' ? '0 0 8px hsla(145,72%,40%,0.35)' : 'none' }} />
                      ))
                   ) : (
                     <Typography color="text.secondary">No attributes listed.</Typography>
                   )}
                </Stack>
              </Paper>

              {/* Documents Section - Blurred for public users */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <DescriptionIcon color="action" /> Uploaded Documents
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {blurredSections?.documents ? (
                  <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    <LockIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1.5, opacity: 0.8 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 3 }}>
                      {loginMessage || 'Documents are hidden. Login to see more information.'}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => loginWithRedirect({ appState: { redirect_uri: window.location.pathname } })}
                        sx={{ 
                          fontWeight: 600,
                          borderColor: 'secondary.main',
                          '&:hover': {
                            bgcolor: 'rgba(238, 166, 43, 0.08)',
                            borderColor: 'secondary.main',
                          }
                        }}
                      >
                        Log In
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => signupWithRedirect({ appState: { redirect_uri: window.location.pathname } })}
                        sx={{ 
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #eea62b, #d48d11)',
                          boxShadow: (theme) => `0 0 16px ${theme.palette.secondary.main}30`,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #eea62b, #d48d11)',
                            boxShadow: (theme) => `0 0 24px ${theme.palette.secondary.main}40`,
                          }
                        }}
                      >
                        Sign Up
                      </Button>
                    </Stack>
                  </Box>
                ) : documentsList.length > 0 ? (
                  <List dense disablePadding>
                    {documentsList.map((doc) => {
                      const iconMap = {
                        id_photo: <BadgeIcon color="action" />,
                        white_card: <CardMembershipIcon color="action" />,
                        ticket_license_certificate: <InsertDriveFileIcon color="action" />,
                      };
                      const labelMap = {
                        id_photo: 'ID Photo',
                        white_card: 'White Card',
                        ticket_license_certificate: doc.documentLabel || 'Ticket / License / Certificate',
                      };
                      return (
                        <ListItem
                          key={doc.id}
                          disableGutters
                          button
                          onClick={() => isAdmin && handleAdminPreview(doc)}
                          sx={{ cursor: isAdmin ? 'pointer' : 'default' }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {iconMap[doc.documentType] || <InsertDriveFileIcon color="action" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={labelMap[doc.documentType] || doc.documentType}
                            secondary={doc.ticketNumber ? `Ticket #: ${doc.ticketNumber}` : null}
                          />
                          {isAdmin && (
                            <Stack direction="row" spacing={0.5}>
                              {isPreviewableFile(doc.mimeType, doc.fileName) && (
                                <Tooltip title="Preview document">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={(e) => { e.stopPropagation(); handleAdminPreview(doc); }}
                                      disabled={downloadingDocId === doc.id}
                                    >
                                      {downloadingDocId === doc.id ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <VisibilityIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              <Tooltip title="Download document">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => { e.stopPropagation(); handleAdminDownload(doc.id); }}
                                    disabled={downloadingDocId === doc.id}
                                  >
                                    {downloadingDocId === doc.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography color="text.secondary">No documents uploaded.</Typography>
                )}
              </Paper>
            </Stack>
          </Grid>

          {/* Sidebar Column */}
          <Grid size={{ xs: 12, md: 4 }}>
             <Paper
               elevation={0}
               sx={{
                 p: 3,
                 borderRadius: 2,
                 height: '100%',
                 position: 'relative',
               }}
             >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Profile Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {blurredSections?.experience ? (
                  <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                    <LockIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2, opacity: 0.8 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 3 }}>
                      {loginMessage || 'Login as a company to see the full profile and connect with this worker.'}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => loginWithRedirect({ appState: { redirect_uri: window.location.pathname } })}
                        sx={{ 
                          fontWeight: 600,
                          borderColor: 'secondary.main',
                          '&:hover': {
                            bgcolor: 'rgba(238, 166, 43, 0.08)',
                            borderColor: 'secondary.main',
                          }
                        }}
                      >
                        Log In
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => signupWithRedirect({ appState: { redirect_uri: window.location.pathname } })}
                        sx={{ 
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #eea62b, #d48d11)',
                          boxShadow: (theme) => `0 0 16px ${theme.palette.secondary.main}30`,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #eea62b, #d48d11)',
                            boxShadow: (theme) => `0 0 24px ${theme.palette.secondary.main}40`,
                          }
                        }}
                      >
                        Sign Up
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <DetailRow 
                      label="Experience Level"
                      value={getLabel(jobSeekerData?.experience, experienceOptions) || 'Not specified'}
                      icon={<WorkIcon />}
                    />

                    <DetailRow 
                      label="Availability"
                      value={getLabel(jobSeekerData?.availability, availabilityOptions) || 'Not specified'}
                    />

                    <DetailRow 
                      label="Expected Hourly Rate"
                      value={getLabel(jobSeekerData?.expectedSalary, salaryOptions) || 'Negotiable'}
                      icon={<AttachMoneyIcon />}
                    />

                    <DetailRow 
                      label="Transport"
                      value={getLabel(jobSeekerData?.transport, transportOptions) || 'Not specified'}
                      icon={<DriveEtaIcon />}
                    />

                    <DetailRow 
                      label="Visa Status"
                      value={getLabel(jobSeekerData?.visaStatus, visaOptions) || 'Unknown'}
                      chipColor={['citizen', 'pr'].includes(jobSeekerData?.visaStatus) ? 'success' : 'warning'}
                    />

                    <DetailRow 
                      label="ABN Status"
                      value={jobSeekerData?.hasValidABN ? 'Valid ABN' : 'No ABN'}
                      chipColor={jobSeekerData?.hasValidABN ? 'success' : 'warning'}
                    />

                    {jobSeekerData?.hasValidABN && jobSeekerData?.abnNumber && (
                      <DetailRow 
                        label="ABN Number"
                        value={jobSeekerData.abnNumber}
                      />
                    )}

                    {jobSeekerData?.countryOfOrigin && (
                      <DetailRow 
                        label="Country of Origin"
                        value={jobSeekerData.countryOfOrigin}
                        icon={<PublicIcon />}
                      />
                    )}

                    {jobSeekerData?.nativeLanguage && (
                      <DetailRow 
                        label="Native Language"
                        value={jobSeekerData.nativeLanguage}
                        icon={<TranslateIcon />}
                      />
                    )}

                    {jobSeekerData?.englishLevel && (
                      <DetailRow 
                        label="English Level"
                        value={getLabel(jobSeekerData.englishLevel, englishLevelOptions) || jobSeekerData.englishLevel}
                        icon={<LanguageIcon />}
                      />
                    )}
                  </Stack>
                )}
             </Paper>
          </Grid>
        </Grid>

        {/* Admin Actions Section */}
        {isAdmin && (
          <Paper
            elevation={0}
            sx={{ p: 3, mt: 3, borderRadius: 2 }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Admin Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<VisibilityIcon />}
              onClick={handleManageJobseeker}
            >
              Manage Jobseeker
            </Button>
          </Paper>
        )}
      </Container>

      {isCompany && (
        <FeedbackDialog
          open={feedbackDialogOpen}
          onClose={() => setFeedbackDialogOpen(false)}
          ratedProfileType="jobseeker"
          ratedProfileId={id}
          ratedName={profileData?.firstName || 'this worker'}
          raterProfileType="company"
          raterProfileId={userTypeData?.profiles?.company?.id}
          raterName={userTypeData?.profiles?.company?.companyName || ''}
          jobId={jobId}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <FilePreviewDialog
        open={Boolean(previewDocument)}
        onClose={() => setPreviewDocument(null)}
        title={previewDocument?.documentLabel || previewDocument?.fileName}
        fileName={previewDocument?.fileName}
        mimeType={previewDocument?.mimeType}
        downloadUrl={previewDocument?.downloadUrl}
      />
    </Box>
  );
};

export default JobSeekerPublicPage;
